import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Box, Tabs, Tab, useMediaQuery, Menu, MenuItem, Checkbox, tabsClasses } from '@mui/material';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useThemes } from '../Theme/theme';

const Menubar = React.memo(function Menubar({
  setProfitnLossClicked,
  setBusinessOverviewCkicked,
  setBalanceSheetCkicked,
  setReceivableClicked,
  setPayableClicked,
  setTrailBalanceClicked,
  setItemsClicked,
}) {
  const { theme } = useThemes();
  const smallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [value, setValue] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const showMenu = Boolean(anchorEl);

  const [showReceivable, setShowReceivable] = useState(false);
  const [showPayable, setShowPayable] = useState(false);

  const clickMap = useMemo(
    () => ({
      Business_Overview: setBusinessOverviewCkicked,
      ProfitAndLoss: setProfitnLossClicked,
      BalanceSheet: setBalanceSheetCkicked,
      TrialBalance: setTrailBalanceClicked,
      Items: setItemsClicked,
    }),
    [setBusinessOverviewCkicked, setProfitnLossClicked, setBalanceSheetCkicked, setTrailBalanceClicked, setItemsClicked]
  );

  const resetClicks = useCallback(() => {
    setBusinessOverviewCkicked(false);
    setProfitnLossClicked(false);
    setBalanceSheetCkicked(false);
    setReceivableClicked(false);
    setPayableClicked(false);
    setTrailBalanceClicked(false);
    setItemsClicked(false);
  }, [
    setBusinessOverviewCkicked,
    setProfitnLossClicked,
    setBalanceSheetCkicked,
    setReceivableClicked,
    setPayableClicked,
    setTrailBalanceClicked,
    setItemsClicked,
  ]);

  useEffect(() => {
    let active = searchParams.get('active') || 'Business_Overview';
    // if (!searchParams.get('active')) {
    //   setSearchParams({ active });
    // }
    if (active === 'Receivables') {
      setShowReceivable(true);
      setShowPayable(false);
      setValue('Outstanding');
    } else if (active === 'Payables') {
      setShowPayable(true);
      setShowReceivable(false);
      setValue('Outstanding');
    } else {
      setShowReceivable(false);
      setShowPayable(false);
      setValue(active);
    }
  }, [searchParams, setSearchParams]);

  const handleTabChange = useCallback(
    (event, newValue) => {
      if (newValue === 'Report') {
        navigate('/reports');
        return;
      }

      if (newValue === 'Outstanding') {
        return showMenu ? setAnchorEl(null) : setAnchorEl(event.currentTarget);
      }
      resetClicks();

      setAnchorEl(null);
      setSearchParams({ active: newValue });
      clickMap[newValue]?.(true);
    },
    [clickMap, resetClicks, setSearchParams, showMenu, navigate]
  );

  const handleCheckboxChange = useCallback(
    (_, type) => {
      resetClicks();
      if (type === 'Receivables') {
        setShowReceivable(true);
        setShowPayable(false);
        setReceivableClicked(true);
      } else {
        setShowPayable(true);
        setShowReceivable(false);
        setPayableClicked(true);
      }
      setSearchParams({ active: type });
      setAnchorEl(null);
    },
    [resetClicks, setPayableClicked, setReceivableClicked, setSearchParams]
  );

  return (
    <Box sx={{ width: '100%', mt: 1, borderRadius: 1, overflow: 'auto' }}>
      <Tabs
        variant={smallScreen ? 'scrollable' : 'fullWidth'}
        allowScrollButtonsMobile
        value={value}
        onChange={handleTabChange}
        sx={{
          [`& .${tabsClasses.scrollButtons}.Mui-disabled`]: { opacity: 0.3 },
          '& .MuiTab-root': {
            fontWeight: 'bold',
            fontSize: 14,
            color: theme.palette.text.textClr,
            textTransform: 'none',
          },
          '& .Mui-selected': {
            color: theme.palette.sideBar.main,
            fontSize: 17,
          },
          borderRadius: 1,
          transition: 'all 1s ease-in-out',
         
        }}
      >
        <Tab label="BizView" value="Business_Overview" />
        <Tab label="Profit & Loss" value="ProfitAndLoss" />
        <Tab label="Balance Sheet" value="BalanceSheet" className='BalanceSheet'/>
        <Tab label="Trial Balance" value="TrialBalance" />
        <Tab
          label="Trade Accounts ▼"
          value="Outstanding"
          aria-controls="menu-outstanding"
          aria-haspopup="true"
          onClick={(e) => handleTabChange(e, 'Outstanding')}
          sx={{
            '&:hover': {
              cursor: 'pointer',
            },
             whiteSpace:"nowrap"
          }}
        />{' '}
        <Tab label="Item" value="Items" />
        <Tab label="Reports" value="Report" />
      </Tabs>

      <Menu
        id="more-menu"
        anchorEl={anchorEl}
        open={showMenu}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        PaperProps={{
          sx: {
            minWidth: 180,
            p: 0.8,
          },
        }}
      >
        <MenuItem
          sx={{
            mb: 0.5, 
            borderRadius: 1,
            fontWeight: 'bold',
            fontSize: '15px',
            backgroundColor: showReceivable ? theme.palette.sideBar.main + '22' : 'transparent', 
            color: showReceivable ? '#000' : 'inherit',
            '&:hover': {
              backgroundColor: theme.palette.sideBar.main + '22',
            },
          }}
          onClick={(e) => handleCheckboxChange(e, 'Receivables')}
        >
          Trade Receivables
        </MenuItem>
        <MenuItem
          sx={{
            borderRadius: 1,
            fontWeight: 'bold',
            fontSize: '15px',
            backgroundColor: showPayable ? theme.palette.sideBar.main + '22' : 'transparent',
            color: showPayable ? '#000' : 'inherit',
            '&:hover': {
              backgroundColor: theme.palette.sideBar.main + '22',
            },
          }}
          onClick={(e) => handleCheckboxChange(e, 'Payables')}
        >
          Trade Payables
        </MenuItem>
      </Menu>
    </Box>
  );
});

export default Menubar;
